import { Box, Button, HStack, VStack } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type NavigateFunction, useBlocker } from 'react-router'

import type { ToastLike } from '@mangovoice/mango-ui'

import { DevicesApiProvider } from '../api/context'
import type { DevicesApi } from '../api/devicesApi'
import {
  DeviceButtonGrid,
  DeviceForm,
  type DeviceFormHandle,
} from '../components'
import { useCreateDevice, useGetDeviceModels } from '../hooks'
import { DevicesI18nProvider, useDevicesI18n } from '../i18n'
import type { DevicesTranslationLike } from '../i18n'
import type { DeviceButtonPayload } from '../types'
import {
  getLegacyDefaultMainButtons,
  hasInvalidDeviceButtonConfiguration,
  sanitizeDeviceButtonsForApi,
  TOAST_DURATION_SUCCESS,
} from '../utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DeviceNewPageProps {
  api: DevicesApi
  pbxId: number
  navigate: NavigateFunction
  toast: ToastLike
  listRoute: string
  wrapper?: React.ComponentType<{ children: React.ReactNode }>
  UnsavedChangesDialog?: React.ComponentType<{
    blocker: ReturnType<typeof useBlocker>
  }>
  /** Optional translation function for i18n; when omitted, components use fallback English */
  t?: DevicesTranslationLike
  /** Mirrors legacy behavior for internal-only fields (Warranty/Source). */
  showInternalFields?: boolean
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const DeviceNewPage = (props: DeviceNewPageProps) => {
  return (
    <DevicesApiProvider api={props.api}>
      <DevicesI18nProvider t={props.t}>
        <DeviceNewPageInner {...props} />
      </DevicesI18nProvider>
    </DevicesApiProvider>
  )
}

const DeviceNewPageInner = ({
  pbxId,
  navigate,
  toast,
  listRoute,
  wrapper: Wrapper,
  UnsavedChangesDialog,
  showInternalFields = true,
}: DeviceNewPageProps) => {
  const { text } = useDevicesI18n()
  const { data: models = [] } = useGetDeviceModels()
  const createDevice = useCreateDevice(pbxId)

  const formRef = useRef<DeviceFormHandle>(null)
  const [canSubmit, setCanSubmit] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const blocker = useBlocker(hasUnsavedChanges)

  const [selectedModelId, setSelectedModelId] = useState<number | null>(null)
  const [sidecarEnabled, setSidecarEnabled] = useState(false)
  const [primaryExtensionId, setPrimaryExtensionId] = useState<number | null>(
    null,
  )
  const [lineButtonCount, setLineButtonCount] = useState<number | null>(null)
  const [hasButtonValidationError, setHasButtonValidationError] =
    useState(false)

  const handleModelChange = useCallback((modelId: number | null) => {
    setSelectedModelId(modelId)
  }, [])

  const handleSidecarChange = useCallback((enabled: boolean) => {
    setSidecarEnabled(enabled)
  }, [])

  const model = selectedModelId
    ? (models.find((m) => m.id === selectedModelId) ?? null)
    : null

  const [buttons, setButtons] = useState<DeviceButtonPayload[]>([])
  const prevModelIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (!model) {
      prevModelIdRef.current = null
      setButtons([])
      return
    }

    if (prevModelIdRef.current === model.id) return
    prevModelIdRef.current = model.id
    setButtons(getLegacyDefaultMainButtons())
  }, [model?.id])

  const handleButtonsChange = useCallback(
    (newButtons: DeviceButtonPayload[]) => {
      setButtons(newButtons)
    },
    [],
  )

  const hasInvalidButtonConfiguration = useMemo(
    () =>
      hasInvalidDeviceButtonConfiguration(buttons, {
        includeSidecarButtons: sidecarEnabled,
        maxLineButtons: lineButtonCount ?? undefined,
      }),
    [buttons, lineButtonCount, sidecarEnabled],
  )

  const hasButtonValidationProblem =
    hasButtonValidationError || hasInvalidButtonConfiguration

  const handleSubmit = (
    payload: Parameters<typeof createDevice.mutateAsync>[0],
    _reboot?: boolean,
  ) => {
    if (hasButtonValidationProblem) {
      setHasButtonValidationError(true)
      return
    }

    const submitButtons = sanitizeDeviceButtonsForApi(buttons, {
      primaryExtensionId: payload.primary_extension_id,
      includePrimaryLineFallback: true,
      includeSidecarButtons: sidecarEnabled,
      maxLineButtons: lineButtonCount ?? undefined,
    })

    createDevice.mutate(
      {
        ...payload,
        ...(submitButtons.length > 0 ? { buttons: submitButtons } : {}),
      },
      {
        onSuccess: () => {
          setHasUnsavedChanges(false)
          toast.create({
            title: text('devices.toast.deviceCreated', 'Device created'),
            type: 'success',
            duration: TOAST_DURATION_SUCCESS,
          })
          navigate(listRoute)
        },
        onError: (error) => {
          toast.create({
            title: text(
              'devices.toast.createFailed',
              'Failed to create device',
            ),
            description: error?.message ?? 'Unknown error',
            type: 'error',
          })
        },
      },
    )
  }

  const handleCancel = () => {
    navigate(listRoute)
  }

  const isSubmitting = createDevice.isPending

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return
    const target = e.target as HTMLElement
    if (target.tagName === 'TEXTAREA') return
    e.preventDefault()
    formRef.current?.submit(false)
  }, [])

  if (!pbxId) {
    return null
  }

  const content = (
    <Box flex={1} overflow="auto" p={8} onKeyDown={handleKeyDown}>
      <VStack align="stretch" gap={6}>
        <DeviceForm
          ref={formRef}
          mode="add"
          pbxId={pbxId}
          device={null}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
          onModelChange={handleModelChange}
          onSidecarChange={handleSidecarChange}
          onPrimaryExtensionChange={setPrimaryExtensionId}
          hideActions
          onCanSubmitChange={setCanSubmit}
          onDirtyChange={setHasUnsavedChanges}
          onLineButtonCountChange={setLineButtonCount}
          showInternalFields={showInternalFields}
        />
        {model && (
          <DeviceButtonGrid
            pbxId={pbxId}
            sidecarEnabled={sidecarEnabled}
            primaryExtensionId={primaryExtensionId}
            model={model}
            buttons={buttons}
            onChange={handleButtonsChange}
            lineButtonCount={lineButtonCount ?? undefined}
            onButtonValidationError={setHasButtonValidationError}
          />
        )}
        <Box
          p={6}
          borderTopWidth={1}
          borderColor="border.default"
          bg="bg.panel"
          borderRadius="lg"
        >
          <HStack gap={3} justifyContent="flex-end">
            <Button
              type="button"
              variant="outline"
              colorPalette="neutral"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {text('devices.common.cancel', 'Cancel')}
            </Button>
            <Button
              type="button"
              colorPalette="brand"
              onClick={() => formRef.current?.submit(false)}
              loading={isSubmitting}
              disabled={!canSubmit || hasButtonValidationProblem}
            >
              {text('devices.form.createDevice', 'Create device')}
            </Button>
          </HStack>
        </Box>
      </VStack>
      {UnsavedChangesDialog && <UnsavedChangesDialog blocker={blocker} />}
    </Box>
  )

  return Wrapper ? <Wrapper>{content}</Wrapper> : content
}
